// Home's Last trip card: the newest finished trip. A trips row (037) knows the
// store and the cost; the item history knows the rest (recapOfTrip), and is
// the only record of trips from before the trips table, so whichever is newer
// wins and a trip found only in the history has no store or cost.

import type { Trip } from './supabase'
import { deletedFinishes, pastTrips, recapOfTrip, type TripRecap } from './trips'
import { dateLabel, dayKey } from './mealPlan'

type HistoryRow = Parameters<typeof recapOfTrip>[1][number]

export interface LastTrip {
  finishedAt: Date
  /** The trips row, for its summary's cost question; none for a history-only trip. */
  tripId?: string
  /** Who finished it: the cost question is theirs (046). Null before 045 or history-only. */
  finishedBy?: string | null
  storeName: string | null
  cost: { amount: number; currency: string } | null
  recap: TripRecap
}

export function lastTrip(trips: Trip[], rows: HistoryRow[], storeName: (id: string | null) => string | null): LastTrip | null {
  const deleted = deletedFinishes(trips)
  const row = trips
    .filter(t => t.finished_at && !t.deleted_at)
    .sort((a, b) => Date.parse(b.finished_at!) - Date.parse(a.finished_at!))[0]
  const history = pastTrips(rows, deleted)[0]
  if (row && (!history || Date.parse(row.finished_at!) >= history.finishedAt)) {
    return {
      finishedAt: new Date(row.finished_at!),
      tripId: row.id,
      finishedBy: row.finished_by,
      storeName: storeName(row.store_id),
      cost: row.cost != null && row.currency ? { amount: row.cost, currency: row.currency } : null,
      recap: recapOfTrip(row.finished_at!, rows, deleted),
    }
  }
  if (!history) return null
  const at = new Date(history.finishedAt)
  return { finishedAt: at, storeName: null, cost: null, recap: recapOfTrip(at.toISOString(), rows, deleted) }
}

const DAY_MS = 86_400_000
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/** "Today", "Yesterday", a weekday within the week, else "12 Oct" (as the Plan tab writes dates). */
export function whenLabel(at: Date, now: Date): string {
  const days = Math.round((startOfDay(now) - startOfDay(at)) / DAY_MS)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return at.toLocaleDateString('en', { weekday: 'long' })
  return dateLabel(dayKey(at))
}
